import { Box } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DenyButton from "../../common/buttons/DenyButton";
import AcceptButton from "../../common/buttons/AcceptButton";
import { MedicalIndicatorsEnum } from "../../../models/enums/medicalIndicatorsEnum";
import { MedicalCheckupDto } from "../../../models/medicalCheckups/medicalCheckupDto";
import { MedicalCheckupResult } from "../../../models/enums/medicalCheckupResult";
import { MedicalCheckupVerdictDto } from "../../../models/medicalCheckups/medicalCheckupVerdictDto";
import { medicalCheckupSigningService, ValidationError, WorkplaceError, NetworkError } from "../../../services/medicalCheckupSigningService";
import { NotAllowedDialog } from "../NotAllowedDialog";
import { ConfirmDialog } from "../../common/ConfirmDialog";
import { CertificateSelectionDialog } from "../../common/CertificateSelectionDialog";
import type { Certificate } from "../../../models/digitalSignatures/cryptoProTypes";
import { MedicDigitalSignDto } from "../../../models/medics/medicDigitalSignDto";
import { ROUTES } from "../../../constants/routes";

interface PhysicalTokenSigningButtonsProps {
    medicalCheckup: MedicalCheckupDto;
    disabled?: boolean;
    defaultCertificate: MedicDigitalSignDto | null;
    onValidationError?: (message: string) => void;
    onWorkplaceError?: (message: string, retry?: () => Promise<void>) => void;
    onNetworkError?: (message: string) => void;
    onLoadingChange?: (loading: boolean, spinnerText?: string) => void;
}

export const PhysicalTokenSigningButtons: React.FC<PhysicalTokenSigningButtonsProps> = ({
    medicalCheckup,
    disabled = false,
    defaultCertificate,
    onValidationError,
    onWorkplaceError,
    onNetworkError,
    onLoadingChange,
}) => {
    const [openNotAllowedDialog, setOpenNotAllowedDialog] = useState(false);
    const [openAllowConfirmationDialog, setOpenAllowConfirmationDialog] = useState(false);
    const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
    const [pendingVerdictData, setPendingVerdictData] = useState<MedicalCheckupVerdictDto | null>(null);
    const navigate = useNavigate();

    const hasRedValuesOrComplaints = () => {
        if (!medicalCheckup) return false;

        // Check for complaints
        if (medicalCheckup.complaints && medicalCheckup.complaints.length > 0) {
            return true;
        }

        // Check for red medical indicators
        const indicators = medicalCheckup.indicators;
        return (
            indicators.alcoholParam === MedicalIndicatorsEnum.High ||
            indicators.upperPressureParam === MedicalIndicatorsEnum.High ||
            indicators.lowerPressureParam === MedicalIndicatorsEnum.High ||
            indicators.pulseParam === MedicalIndicatorsEnum.High ||
            indicators.temperatureParam === MedicalIndicatorsEnum.High
        );
    };

    const performSign = async (
        verdictData: MedicalCheckupVerdictDto,
        thumbprint: string,
        certificate: MedicDigitalSignDto | null
    ) => {
        onLoadingChange?.(true, "Подписание...");

        try {
            await medicalCheckupSigningService.signWithPhysicalToken(
                medicalCheckup.id,
                verdictData,
                thumbprint,
                certificate,
                (progress) => {
                    onLoadingChange?.(true, progress.message);
                }
            );

            // Успешное подписание
            navigate(ROUTES.medicalCheckups.listOfNew);
        } catch (e: any) {
            console.error(e);

            const retry = () => performSign(verdictData, thumbprint, certificate);

            if (e instanceof ValidationError) {
                onValidationError?.(e.message);
            } else if (e instanceof NetworkError) {
                onNetworkError?.(e.message);
            } else if (e instanceof WorkplaceError) {
                onWorkplaceError?.(e.message, retry);
            } else {
                if (e.response) {
                    const errorMessage = e.response?.data?.message ?? e.message ?? "Ошибка валидации";
                    onValidationError?.(errorMessage);
                } else {
                    const errorMessage = e.message ?? "Непредвиденная ошибка";
                    onWorkplaceError?.(errorMessage, retry);
                }
            }

            onLoadingChange?.(false);
        }
    };

    const handleVerdict = async (verdictData: MedicalCheckupVerdictDto) => {
        if (!defaultCertificate) {
            // Нет сохраненного сертификата - показываем диалог выбора
            setPendingVerdictData(verdictData);
            setCertificateDialogOpen(true);
            return;
        }

        await performSign(verdictData, defaultCertificate.thumbprint, defaultCertificate);
    };

    const handleCertificateSelected = async (certificate: Certificate) => {
        if (!pendingVerdictData) {
            setCertificateDialogOpen(false);
            return;
        }

        const verdictData = pendingVerdictData;
        setPendingVerdictData(null);
        setCertificateDialogOpen(false);

        await performSign(verdictData, certificate.thumbprint, null);
    };

    const handleAllow = () => {
        const verdictData: MedicalCheckupVerdictDto = {
            result: MedicalCheckupResult.Approved,
            rejectReasons: []
        };
        
        // If there are red values or complaints, show confirmation dialog
        if (hasRedValuesOrComplaints()) {
            setOpenAllowConfirmationDialog(true);
        } else {
            handleVerdict(verdictData);
        }
    };

    const handleAllowConfirmed = () => {
        setOpenAllowConfirmationDialog(false);
        const verdictData: MedicalCheckupVerdictDto = {
            result: MedicalCheckupResult.Approved,
            rejectReasons: []
        };
        handleVerdict(verdictData);
    };

    const handleNotAllowed = (selectedReasons: number[], comment: string) => {
        setOpenNotAllowedDialog(false);
        const verdictData: MedicalCheckupVerdictDto = {
            result: MedicalCheckupResult.Rejected,
            rejectReasons: selectedReasons,
            comment: comment
        };
        handleVerdict(verdictData);
    };

    return (
        <>
            <Box paddingTop={1} px={2} sx={{ display: 'flex', float: 'right' }}>
                <Box px={2}>
                    <DenyButton 
                        onClick={() => setOpenNotAllowedDialog(true)} 
                        disabled={disabled}
                    >
                        Недопуск
                    </DenyButton>
                </Box>
                <Box>
                    <AcceptButton 
                        onClick={handleAllow} 
                        disabled={medicalCheckup.indicators.alcoholParam === MedicalIndicatorsEnum.High || disabled}
                    >
                        Допуск
                    </AcceptButton>
                </Box>
            </Box>
            <NotAllowedDialog
                medicalCheckupId={medicalCheckup.id}
                open={openNotAllowedDialog}
                onSubmit={handleNotAllowed}
                onClose={() => setOpenNotAllowedDialog(false)}
            />
            <ConfirmDialog
                open={openAllowConfirmationDialog}
                title="Подтверждение допуска"
                content="У водителя есть недопустимые значения или жалобы. Вы уверены, что хотите допустить сотрудника?"
                onClose={() => setOpenAllowConfirmationDialog(false)}
                onConfirm={handleAllowConfirmed}
            />
            <CertificateSelectionDialog
                open={certificateDialogOpen}
                onClose={() => {
                    setCertificateDialogOpen(false);
                    setPendingVerdictData(null);
                    onLoadingChange?.(false);
                }}
                onSelect={handleCertificateSelected}
            />
        </>
    );
};

